// src/pages/sm/StaffGroups.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Typography, Button, Card, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Stack, Tooltip, Chip, Grid,
    FormControlLabel, Checkbox, Paper
} from "@mui/material";
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Policy as PolicyIcon,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import AppHeader from '../../components/AppHeader';

const StaffGroups = ({ setIsLoggedIn }) => {
    const [groups, setGroups] = useState([]);
    const [, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);

    const [formData, setFormData] = useState({
        group_code: '',
        group_name: '',
        description: '',
        is_active: 1,
        exclude_public_holiday: 0,
        exclude_company_holiday: 0
    });

    const fetchGroups = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_API_URL1}/sm/groups`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                setGroups(res.data.data);
            }
        } catch (err) {
            console.error("Error fetching groups:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchGroups();
    }, [fetchGroups]);

    const handleOpenDialog = useCallback((group = null) => {
        if (group) {
            setEditingGroup(group);
            setFormData({
                group_code: group.group_code,
                group_name: group.group_name,
                description: group.description || '',
                is_active: group.is_active,
                exclude_public_holiday: group.exclude_public_holiday || 0,
                exclude_company_holiday: group.exclude_company_holiday || 0
            });
        } else {
            setEditingGroup(null);
            setFormData({
                group_code: '',
                group_name: '',
                description: '',
                is_active: 1,
                exclude_public_holiday: 0,
                exclude_company_holiday: 0
            });
        }
        setOpenDialog(true);
    }, []);

    const handleCloseDialog = useCallback(() => {
        setOpenDialog(false);
        setEditingGroup(null);
    }, []);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
        }));
    };

    const handleSubmit = useCallback(async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL1}/sm/groups${editingGroup ? `/${editingGroup.id}` : ''}`;
            const method = editingGroup ? 'put' : 'post';

            const res = await axios[method](url, formData, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });

            if (res.data.success) {
                fetchGroups();
                handleCloseDialog();
            }
        } catch (err) {
            console.error("Error saving group:", err);
            alert(err.response?.data?.message || "Error saving group");
        }
    }, [editingGroup, formData, fetchGroups, handleCloseDialog]);

    const handleDelete = useCallback(async (id) => {
        if (!window.confirm("Are you sure you want to delete this group?")) return;
        try {
            const res = await axios.delete(`${process.env.REACT_APP_API_URL1}/sm/groups/${id}`, {
                withCredentials: true,
                headers: { 'X-Tenant': localStorage.getItem('tenant') }
            });
            if (res.data.success) {
                fetchGroups();
            }
        } catch (err) {
            console.error("Error deleting group:", err);
            alert(err.response?.data?.message || "Error deleting group");
        }
    }, [fetchGroups]);

    const columnDefs = useMemo(() => [
        {
            headerName: "Code",
            field: "group_code",
            width: 150,
            cellStyle: { fontWeight: 'bold' }
        },
        { headerName: "Group Name", field: "group_name", flex: 1 },
        {
            headerName: "PH Entitled",
            field: "exclude_public_holiday",
            width: 130,
            cellRenderer: params => (
                <Chip
                    label={params.value === 1 ? "No" : "Yes"}
                    size="small"
                    color={params.value === 1 ? "error" : "success"}
                    sx={{ fontWeight: 600 }}
                />
            )
        },
        {
            headerName: "CH Entitled",
            field: "exclude_company_holiday",
            width: 130,
            cellRenderer: params => (
                <Chip
                    label={params.value === 1 ? "No" : "Yes"}
                    size="small"
                    color={params.value === 1 ? "error" : "success"}
                    sx={{ fontWeight: 600 }}
                />
            )
        },
        {
            headerName: "Status",
            field: "is_active",
            width: 110,
            cellRenderer: params => (
                <Chip
                    label={params.value === 1 ? "Active" : "Inactive"}
                    size="small"
                    color={params.value === 1 ? "success" : "default"}
                    sx={{ fontWeight: 600 }}
                />
            )
        },
        {
            headerName: "Actions",
            width: 120,
            pinned: 'right',
            cellRenderer: params => (
                <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleOpenDialog(params.data)}>
                            <EditIcon fontSize="small" color="primary" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(params.data.id)}>
                            <DeleteIcon fontSize="small" color="error" />
                        </IconButton>
                    </Tooltip>
                </Stack>
            )
        }
    ], [handleDelete, handleOpenDialog]);

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setIsLoggedIn(false);
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader onLogout={handleLogout} />

            <Box sx={{ p: 4, maxWidth: "1600px", mx: "auto" }}>
                <Card sx={{ p: 4, borderRadius: 4, boxShadow: '0 4px 12px 0 rgba(0,0,0,0.03)', border: '1px solid #edf2f7' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                        <Box>
                            <Typography variant="h4" sx={{ color: "#1a237e", fontWeight: 800, mb: 0.5 }}>
                                Staff Groups
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Segment staff for specific holiday and attendance policies
                            </Typography>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenDialog()}
                            sx={{
                                bgcolor: '#1a237e',
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 600,
                                px: 3,
                                '&:hover': { bgcolor: '#0d47a1' }
                            }}
                        >
                            Add Group
                        </Button>
                    </Box>

                    <div className="ag-theme-quartz" style={{ height: 600, width: '100%' }}>
                        <AgGridReact
                            rowData={groups}
                            columnDefs={columnDefs}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true
                            }}
                            pagination={true}
                            paginationPageSize={10}
                            animateRows={true}
                        />
                    </div>
                </Card>
            </Box>

            {/* GROUP DIALOG */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#1a237e' }}>
                    {editingGroup ? "Edit Staff Group" : "Create New Group"}
                </DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={3} sx={{ mt: 0.5 }}>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Group Code</Typography>
                            <TextField
                                fullWidth
                                name="group_code"
                                placeholder="e.g. FACTORY_STAFF"
                                value={formData.group_code}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Group Name</Typography>
                            <TextField
                                fullWidth
                                name="group_name"
                                placeholder="e.g. Factory Workers"
                                value={formData.group_name}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" fontWeight={600} gutterBottom>Description</Typography>
                            <TextField
                                fullWidth
                                multiline
                                rows={2}
                                name="description"
                                placeholder="Purpose of this group..."
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </Grid>

                        {/* POLICY SUBSECTION */}
                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#1a237e' }}>
                                    <PolicyIcon fontSize="small" /> Group Policies
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="exclude_public_holiday"
                                                    checked={formData.exclude_public_holiday === 1}
                                                    onChange={handleChange}
                                                    color="error"
                                                />
                                            }
                                            label={<Typography variant="body2">Exclude from Public Holidays</Typography>}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    name="exclude_company_holiday"
                                                    checked={formData.exclude_company_holiday === 1}
                                                    onChange={handleChange}
                                                    color="error"
                                                />
                                            }
                                            label={<Typography variant="body2">Exclude from Company Holidays</Typography>}
                                        />
                                    </Grid>
                                </Grid>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    HINT: Check these if the group is NOT entitled to holidays (e.g. 24/7 Security).
                                </Typography>
                            </Paper>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="is_active"
                                        checked={formData.is_active === 1}
                                        onChange={handleChange}
                                        color="success"
                                    />
                                }
                                label={<Typography fontWeight={600}>Active Group</Typography>}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={handleCloseDialog} color="inherit" sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        sx={{
                            bgcolor: '#1a237e',
                            fontWeight: 700,
                            px: 4,
                            borderRadius: 2,
                            '&:hover': { bgcolor: '#0d47a1' }
                        }}
                    >
                        {editingGroup ? "Update Group" : "Create Group"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default StaffGroups;
